import React, { useState } from 'react';
import Icon from './Icon';
import PlayerManagement from './PlayerManagement';
import Pairings from './Pairings';
import Standings from './Standings';
import TopCut from './TopCut';
import { calculateTieBreakers, generatePairings } from '../utils/tournament';

const TournamentDashboard = ({ tournament, onUpdate, onBack, availablePlayers, onAddPlayer }) => {
  const [view, setView] = useState('main');

  const handleAddPlayer = (name) => {
    const newPlayer = {
      id: Date.now().toString(),
      name,
      matchWins: 0,
      matchLosses: 0,
      matchDraws: 0,
      matchPoints: 0,
      gameWins: 0,
      gameLosses: 0,
      gamesPlayed: 0,
      matchesPlayed: 0,
      omw: 0,
      gwp: 0,
      ogw: 0,
      dropped: false
    };
    
    // Sauvegarder le joueur globalement
    if (onAddPlayer) {
      onAddPlayer(name);
    }
    
    // Ajouter au tournoi
    onUpdate({ ...tournament, players: [...tournament.players, newPlayer] });
  };

  const handleAddExistingPlayer = (player) => {
    const tournamentPlayer = {
      ...player,
      matchWins: 0,
      matchLosses: 0,
      matchDraws: 0,
      matchPoints: 0,
      gameWins: 0,
      gameLosses: 0,
      gamesPlayed: 0,
      matchesPlayed: 0,
      omw: 0,
      gwp: 0,
      ogw: 0,
      dropped: false
    };
    onUpdate({ ...tournament, players: [...tournament.players, tournamentPlayer] });
  };

  const handleRemovePlayer = (playerId) => {
    const updated = tournament.players.filter(p => p.id !== playerId);
    onUpdate({ ...tournament, players: updated });
  };

  const handleDropPlayer = (playerId) => {
    const updated = tournament.players.map(p =>
      p.id === playerId ? { ...p, dropped: true } : p
    );
    onUpdate({ ...tournament, players: updated });
  };

  const regeneratePairings = () => {
    if (tournament.rounds.length === 0) return;
    
    const currentRoundIndex = tournament.rounds.length - 1;
    const currentRound = tournament.rounds[currentRoundIndex];
    
    // Ne régénérer que si aucun match n'est terminé
    const hasCompletedMatches = currentRound.pairings.some(p => p.completed);
    if (hasCompletedMatches) {
      alert('Impossible de refaire les pairings : des résultats ont déjà été enregistrés.');
      return;
    }
    
    const playersWithTB = calculateTieBreakers(tournament.players, tournament.rounds.slice(0, -1));
    const newPairings = generatePairings(playersWithTB, currentRound.number, true); // true pour ajouter l'aléatoire
    
    const updatedRound = {
      ...currentRound,
      pairings: newPairings
    };
    
    const updatedRounds = [...tournament.rounds];
    updatedRounds[currentRoundIndex] = updatedRound;
    
    onUpdate({
      ...tournament,
      rounds: updatedRounds,
      players: playersWithTB
    });
  };

  const startNextRound = () => {
    const playersWithTB = calculateTieBreakers(tournament.players, tournament.rounds);
    const pairings = generatePairings(playersWithTB, tournament.currentRound + 1);
    
    const newRound = {
      number: tournament.currentRound + 1,
      pairings,
      completed: false
    };

    const updatedTournament = {
      ...tournament,
      rounds: [...tournament.rounds, newRound],
      currentRound: tournament.currentRound + 1,
      players: playersWithTB
    };

    onUpdate(updatedTournament);
    
    // Utiliser setTimeout pour s'assurer que l'état est mis à jour avant de changer la vue
    setTimeout(() => {
      setView('pairings');
    }, 100);
  };

  const handleUpdatePairing = (pairingId, p1Wins, p2Wins, draws) => {
    const currentRound = tournament.rounds[tournament.rounds.length - 1];
    const updated = currentRound.pairings.map(p => {
      if (p.id === pairingId) {
        return {
          ...p,
          player1Wins: p1Wins,
          player2Wins: p2Wins,
          draws,
          completed: true
        };
      }
      return p;
    });

    const updatedRounds = tournament.rounds.map((r, idx) =>
      idx === tournament.rounds.length - 1 ? { ...r, pairings: updated } : r
    );

    onUpdate({ ...tournament, rounds: updatedRounds });
  };

  const handleCompleteRound = () => {
    const currentRound = tournament.rounds[tournament.rounds.length - 1];
    let updatedPlayers = [...tournament.players];

    currentRound.pairings.forEach(pairing => {
      const p1 = updatedPlayers.find(p => p.id === pairing.player1Id);
      const p2 = updatedPlayers.find(p => p.id === pairing.player2Id);

      if (p1) {
        p1.gamesPlayed += pairing.player1Wins + pairing.player2Wins + pairing.draws;
        p1.gameWins += pairing.player1Wins;
        p1.gameLosses += pairing.player2Wins;
        p1.matchesPlayed += 1;

        if (pairing.player1Wins > pairing.player2Wins) {
          p1.matchWins += 1;
          p1.matchPoints += 3;
        } else if (pairing.player1Wins < pairing.player2Wins) {
          p1.matchLosses += 1;
        } else {
          p1.matchDraws += 1;
          p1.matchPoints += 1;
        }
      }

      if (p2) {
        p2.gamesPlayed += pairing.player1Wins + pairing.player2Wins + pairing.draws;
        p2.gameWins += pairing.player2Wins;
        p2.gameLosses += pairing.player1Wins;
        p2.matchesPlayed += 1;

        if (pairing.player2Wins > pairing.player1Wins) {
          p2.matchWins += 1;
          p2.matchPoints += 3;
        } else if (pairing.player2Wins < pairing.player1Wins) {
          p2.matchLosses += 1;
        } else {
          p2.matchDraws += 1;
          p2.matchPoints += 1;
        }
      }
    });

    updatedPlayers = calculateTieBreakers(updatedPlayers, tournament.rounds);
    updatedPlayers.sort((a, b) => {
      if (b.matchPoints !== a.matchPoints) return b.matchPoints - a.matchPoints;
      if (b.omw !== a.omw) return b.omw - a.omw;
      if (b.gwp !== a.gwp) return b.gwp - a.gwp;
      return b.ogw - a.ogw;
    });

    const updatedTournament = { ...tournament, players: updatedPlayers };
    onUpdate(updatedTournament);

    // Ne pas passer automatiquement au Top Cut, rester sur le dashboard principal
    setView('main');
  };

  const handleExport = () => {
    const dataStr = JSON.stringify(tournament, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tournament-${tournament.name}-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const imported = JSON.parse(event.target.result);
        onUpdate(imported);
      } catch (error) {
        alert('Erreur lors de l\'import');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  if (view === 'players') {
    return (
      <PlayerManagement
        players={tournament.players}
        onAddPlayer={handleAddPlayer}
        onDropPlayer={handleDropPlayer}
        onRemovePlayer={handleRemovePlayer}
        onBack={() => setView('main')}
        tournamentStarted={tournament.currentRound > 0}
        availablePlayers={availablePlayers}
        onAddExistingPlayer={handleAddExistingPlayer}
      />
    );
  }

  if (view === 'pairings') {
    const currentRound = tournament.rounds && tournament.rounds.length > 0 
      ? tournament.rounds[tournament.rounds.length - 1] 
      : null;
    
    if (!currentRound) {
      setView('main');
      return null;
    }
    
    return (
      <Pairings
        round={currentRound}
        players={tournament.players}
        onUpdatePairing={handleUpdatePairing}
        onCompleteRound={handleCompleteRound}
        onBack={() => setView('main')}
        onRegeneratePairings={regeneratePairings}
      />
    );
  }

  if (view === 'topcut') {
    return (
      <TopCut
        tournament={tournament}
        onUpdate={onUpdate}
        onBack={() => setView('main')}
        onFinishTournament={() => {
          // Marquer le tournoi comme terminé
          const finishedTournament = {
            ...tournament,
            finished: true,
            finishedAt: new Date().toISOString()
          };
          onUpdate(finishedTournament);
          setView('main');
        }}
      />
    );
  }

  if (view === 'standings') {
    return <Standings players={tournament.players} onBack={() => setView('main')} />;
  }

  const activePlayers = tournament.players.filter(p => !p.dropped).length;
  const hasCurrentRound = tournament.rounds && tournament.rounds.length > 0;
  
  // Vérifier si le round actuel est terminé
  const currentRoundCompleted = hasCurrentRound 
    ? tournament.rounds[tournament.rounds.length - 1].pairings.every(p => p.completed)
    : true;
  
  const canStartRound = activePlayers >= 2 && 
                       tournament.currentRound < tournament.swissRounds &&
                       currentRoundCompleted;

  const canStartTopCut = tournament.hasTopCut && 
                        tournament.currentRound >= tournament.swissRounds && 
                        currentRoundCompleted;

  return (
    <div className="min-h-screen p-4">
      <button onClick={onBack} className="mb-4 text-red-500 flex items-center gap-2">
        <Icon d="M15 19l-7-7 7-7" />
        Accueil
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold mb-2">{tournament.name}</h1>
        <div className="flex gap-4 text-sm text-gray-400">
          <span>{activePlayers} joueurs</span>
          <span>•</span>
          <span>Round {tournament.currentRound}/{tournament.swissRounds}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-6">
        <button
          onClick={() => setView('players')}
          className="bg-gray-800 p-4 rounded-lg text-left active:bg-gray-700"
        >
          <div className="text-2xl font-bold mb-1">{activePlayers}</div>
          <div className="text-sm text-gray-400">Joueurs</div>
        </button>
        <button
          onClick={() => setView('standings')}
          className="bg-gray-800 p-4 rounded-lg text-left active:bg-gray-700"
        >
          <div className="text-2xl font-bold mb-1">{tournament.rounds.length}</div>
          <div className="text-sm text-gray-400">Rounds joués</div>
        </button>
      </div>

      <div className="space-y-3 mb-6">
        {tournament.currentRound < tournament.swissRounds && (
          <button
            onClick={startNextRound}
            disabled={!canStartRound}
            className={`w-full py-4 rounded-lg font-semibold flex items-center justify-center gap-2 ${
              canStartRound 
                ? 'bg-red-600 text-white active:bg-red-700' 
                : 'bg-gray-700 text-gray-400 cursor-not-allowed'
            }`}
            title={
              !currentRoundCompleted 
                ? 'Terminez le round actuel avant de démarrer le suivant'
                : activePlayers < 2 
                  ? 'Il faut au moins 2 joueurs pour démarrer un round'
                  : ''
            }
          >
            <Icon d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            {canStartRound 
              ? `Démarrer Round ${tournament.currentRound + 1}`
              : !currentRoundCompleted
                ? 'Round actuel en cours...'
                : `Round ${tournament.currentRound + 1} (bloqué)`
            }
          </button>
        )}

        {canStartTopCut && (
          <button
            onClick={() => setView('topcut')}
            className="w-full bg-yellow-600 text-white py-4 rounded-lg font-semibold active:bg-yellow-700 flex items-center justify-center gap-2"
          >
            <Icon d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
            Démarrer Top {tournament.topCutSize}
          </button>
        )}

        {tournament.topCut && !canStartTopCut && (
          <button
            onClick={() => setView('topcut')}
            className="w-full bg-yellow-600 text-white py-4 rounded-lg font-semibold active:bg-yellow-700 flex items-center justify-center gap-2"
          >
            <Icon d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 713.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 713.138-3.138z" />
            Continuer Top {tournament.topCutSize}
          </button>
        )}

        {hasCurrentRound && (
          <button
            onClick={() => setView('pairings')}
            className="w-full bg-gray-800 text-white py-4 rounded-lg font-semibold active:bg-gray-700"
          >
            Voir les pairings actuels
          </button>
        )}

        <button
          onClick={() => setView('standings')}
          className="w-full bg-gray-800 text-white py-4 rounded-lg font-semibold active:bg-gray-700 flex items-center justify-center gap-2"
        >
          <Icon d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          Classement
        </button>
      </div>

      <div className="border-t border-gray-800 pt-6">
        <h2 className="text-sm font-semibold text-gray-400 mb-3">Données</h2>
        <div className="flex gap-2">
          <button
            onClick={handleExport}
            className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold active:bg-blue-700 text-sm"
          >
            Exporter
          </button>
          <label className="flex-1 bg-purple-600 text-white py-3 rounded-lg font-semibold active:bg-purple-700 text-sm text-center cursor-pointer">
            Importer
            <input type="file" accept=".json" onChange={handleImport} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
};

export default TournamentDashboard;